import type { Role } from '@bm/shared';
import { shuffle, type Rng } from './rng.js';

/**
 * Rollenverteilung für den Ein-Gerät-Modus („nur Rollen verteilen").
 *
 * Bewusst getrennt von `handleStartGame`: dort gehört die Verteilung an einen
 * Raumzustand und passiert ausschließlich serverseitig. Hier gibt es weder Raum
 * noch Server — nur eine Namensliste auf einem Handy. Gemeinsam bleibt das
 * Verfahren: Fisher-Yates über alle Plätze, die ersten `mafiaCount` werden Mafia.
 *
 * Der Zufall wird injiziert (Web-Crypto im Client, `mulberry32` im Test), damit
 * die Verteilung testbar und trotzdem produktiv nicht vorhersagbar ist.
 */
export function dealRoles(playerCount: number, mafiaCount: number, rng: Rng): Role[] {
  const seats = shuffle(
    Array.from({ length: playerCount }, (_, i) => i),
    rng,
  );
  const mafiaSeats = new Set(seats.slice(0, mafiaCount));
  return Array.from({ length: playerCount }, (_, i) => (mafiaSeats.has(i) ? 'MAFIA' : 'CITIZEN'));
}

export interface LocalSeat {
  name: string;
  role: Role;
}

/**
 * Eine komplette lokale Runde: Übergabereihenfolge **und** Rollen.
 *
 * Auch die Reihenfolge wird gemischt, nicht nur die Rolle. Bliebe sie bei der
 * Eingabereihenfolge, wäre bei jeder Runde derselbe Spieler zuerst dran — und
 * wer als Letzter drankommt, könnte aus der Warteposition Schlüsse ziehen.
 */
export function dealSeats(names: readonly string[], mafiaCount: number, rng: Rng): LocalSeat[] {
  const order = shuffle(names, rng);
  const roles = dealRoles(order.length, mafiaCount, rng);
  return order.map((name, index) => ({ name, role: roles[index] ?? 'CITIZEN' }));
}
